/**
 * ONE record, rendered in TWO places — the page and the pane beside the list.
 *
 * Christopher, 2026-08-06, on the generic browser: "detail record not opening
 * next to the list". It was not: `/e/$entity` navigated AWAY to `/e/$entity/$id`
 * while every bespoke desk in the console opened a pane over the grid. Two
 * idioms for the same act, and the generic one — the one every entity without a
 * hand-built desk lands on — had the worse of them: the list you were reading
 * disappeared to show you one of its rows.
 *
 * The fix is not a second record UI. It is this: the record's body — the form,
 * the write-door notice, the save handling — is a component, and the only thing
 * the page and the pane disagree about is the chrome around it. `EntityDetail` renders it as a page, the list's peek renders it as a
 * pane, and there is exactly one place where "what a record looks like" is
 * decided. Cloning it would mean the ref picker, the write-door notice and the
 * save-failure handling each landing on one surface and not the other — which is
 * the divergence the plan for this work opens by naming (three ways to open a
 * record, two full record pages).
 *
 * ── Related items: the CASCADE (2026-08-07) ────────────────────────────────
 * Related renders as stacked sections — populated first and already open — via
 * the same `BandGrid` the relation band uses. It replaced a tab strip, which
 * was a second implementation of the same idea and hid every slot but one.
 *
 * The pane used to carry no relations at all: it was a fixed 500px overlay, and
 * a tab strip over sub-grids at that width is present rather than readable.
 * The pane is now a real, RESIZABLE column of the split — the user sets its
 * width and it is remembered — so the argument for withholding them is gone.
 *
 * `RecordRelations` renders here when `presentation === 'pane'` and a
 * `relations` prop says the caller has room for it. That is the "expanded"
 * half of Christopher's canvas model — detail on the left, related on the right
 * as tabbed grids — arriving inside the pane rather than as a separate mode.
 * `PartyRelations` still does not: it is a richer READING for two entities, and
 * a pane is not where you go to study contact points.
 */
import { useEffect, useState, type ReactNode, useMemo } from 'react'
import { CustomizeRowMenu, ErrorState, LayoutComposer, RecordShell, Select, useToast } from '@solum/ui'
import { applyFormOrder, buildForm, singularize } from '@solum/experience'
import type { Row } from '@solum/sdk'
import { useSchema, useSemantics, useEntityRow, useSaveEntity, useExperience, useCanonicalForm, useLayoutRuleVerdicts, useWriteCapability, useTablePresentation, useRecordTitles } from '../data/queries'
import { EntityForm, type SaveIntent } from '../form/EntityForm'
import { useKbFigureSeam } from './kbFigureAction'
import { RelationCascade } from './RelationCascade'
import { RecordLocation } from './RecordLocation'
import { listSlots, recordPaletteScope } from './relations'
import { declaredRelated, fieldSectionsOnly } from './declaredRelated'
import { useFormOrder } from './useFormOrder'
import { useClearFormLayout, useFormLayoutDefaults, useSetFormLayout } from './useFormLayoutDefaults'
import { applyWhen, referencedRules, whenIssueMessage, type ExperienceDocument } from '@solum/experience'
import { applyFormLayout, formLayout, formOrderFromLayout, layoutFromExperience, resolveFormLayout } from './formLayout'
import { usePersona } from '../app/persona'
import { RecordActions } from './EntityActions'
import { KbArticleProcess } from './KbArticleProcess'
import { KbArticlePreview } from './KbArticlePreview'
import { KbArticleAssets } from './KbArticleAssets'
import { KbArticleHistory } from './KbArticleHistory'
import { openPalette, setPaletteScope } from '../app/paletteScope'
import { RefPicker, refTarget } from '../form/RefPicker'
import { QueryBoundary, queryState } from '../data/QueryBoundary'
import { ReadOnlyNotice, WriteFailureNotice } from '../data/WriteFailure'
import { writeDoorOf, writeDoorReason, capabilityRefusedReason, capabilityUnknownReason } from '../data/writeState'
import { usePersonaList } from '../experience/personas'
import { authoringDocument, useAuthoringTarget } from '../data/authoringTarget'
import { RecordIdentityBadges } from './RecordIdentityBadges'
import { RecordProcess } from './RecordProcess'
import { recordRecent } from '../app/recents'
import { RecordMeasures, useRecordMeasures } from './RecordMeasures'
import { RecordFacts } from './RecordFacts'
import { OrganizationRegistry } from './OrganizationRegistry'
import s from './RecordBody.module.css'
import { RecordSystemInfo } from './RecordSystemInfo'

export interface RecordBodyProps {
  entity: string
  id: string
  /** `page` is the full route; `pane` is the aside beside a grid. */
  presentation: 'page' | 'pane'
  /** Called only after a confirmed write, with its explicit destination. */
  onSaved?: (saved: { id: string; intent: SaveIntent; created: boolean }) => void | Promise<void>
  /**
   * Render the record's related items beside the form.
   *
   * ON by default (2026-08-08), and the history is the reason. It began opt-in
   * while the PAGE rendered the cascade itself, so "off by default" honestly
   * meant "the page already has it". When the cascade moved in here, that
   * sentence stopped being true and the default silently became "nobody has
   * it" — which is how the generic browser's peek, the surface every entity
   * without a hand-built desk lands on, lost its related items while the list's
   * own pane kept them. Two panes, one prop, one of them passing it.
   *
   * A default that only reads correctly next to one caller is a trap for the
   * next caller. So related items are what a record HAS, and a surface that
   * genuinely cannot afford them opts out explicitly and says why.
   *
   * `isNew` is handled below regardless — a record that does not exist yet has
   * nothing to relate to.
   */
  relations?: boolean
  /** open a related record; the pane routes it back into itself */
  onOpenRelated?: (entity: string, id: string, name?: string) => void
  /** the caller's own testid for the record's title (peek and pane each had one) */
  headingTestid?: string
}

/**
 * Resolve the canonical id after the write, refusing to invent one on create.
 * An update already has its route id; a create must receive the server's id or
 * the client cannot safely open the record it just persisted.
 */
export function savedRecordId(currentId: string, isNew: boolean, result: unknown): string {
  if (!isNew) return currentId
  const id = String((result as Row | undefined)?.id ?? '').trim()
  if (!id) throw new Error('The server returned no record id, so the saved record could not be opened safely.')
  return id
}

/** The record's display name, from whichever column the entity uses for one. */
export function headingFor(row: Row, fallback: string, isNew: boolean): string {
  if (isNew) return `New ${fallback}`
  const named = row.name ?? row.subject ?? row.display_name ?? row.legal_name
  const person = [row.given_name, row.family_name].filter(Boolean).join(' ')
  return String(named ?? (person || fallback))
}

/**
 * Per-ENTITY process surfaces (#1744), mounted into `RecordShell`'s `process`
 * slot — the one that has existed since #964 and that nothing passed.
 *
 * The same idiom `ShellDrawer` uses for `RECORD_INSIGHT`, and for the same
 * reason its comment gives: a module says what "process" means for its own
 * record here, so this file dispatches instead of accumulating `if (entity …)`.
 *
 * ── this used to say "the platform has no record-action registrar" ─────────
 * It does now: `core.entity_action` + `core.my_entity_actions` (#1131), and
 * `RecordActions` in the header renders them for EVERY entity. So the generic
 * case has left this map, and the sentence the read-only banner has been saying
 * since #1571 — *"use the actions on its surface"* — is finally true.
 *
 * What stays here is what a flat list of acts genuinely cannot express: a
 * PROCESS. `KbArticleProcess` draws a staged rail from the declared status
 * machine and spells named blocker codes into sentences. Migrating it to the
 * registry would trade a rail for three buttons, which is a worse surface, so
 * it keeps its mount and gains the generic acts above it rather than instead.
 *
 * Each component here still fetches its own governed context and renders only
 * the acts the SERVER names; none of them infers authority.
 */
const RECORD_PROCESS: Record<string, (p: { id: string }) => ReactNode> = {
  service_kb_article: KbArticleProcess,
}

/**
 * The record's own READING, where the entity has one — the same dispatch idiom,
 * a different question. `RECORD_PROCESS` answers "what may I do to this?";
 * this answers "what does this actually look like to the person it is for?".
 *
 * Kept separate rather than folded into the process component because they have
 * different lifetimes: the acts change when authority or status changes, and the
 * reading changes when the body does. One component would re-render the whole
 * article every time a publish resolved.
 */
const RECORD_READING: Record<string, (p: { id: string }) => ReactNode> = {
  /* #2267 ports 1+2. What an external REGISTER says this organisation is —
     VAT verification and company-register adoption — arriving through the same
     dispatch as every other record-specific reading, so `RecordBody` still has
     no `if (entity === …)` in it. */
  core_organization: OrganizationRegistry,
  service_kb_article: (p) => (
    <>
      <KbArticleAssets id={p.id} />
      <KbArticlePreview id={p.id} />
      <KbArticleHistory id={p.id} />
    </>
  ),
}

/**
 * The record's FIELD-LEVEL offers, where the entity has any.
 *
 * A third member of the `RECORD_PROCESS` / `RECORD_READING` family and, like
 * them, a console-side dispatch rather than a registrar — the platform has no
 * record-action registrar, and inventing one is a Forge decision.
 *
 * A HOOK rather than a map, because unlike the other two this one has to READ
 * before it can decide what to offer: which figures exist, and whether the
 * caller may write them. It is called unconditionally and gates its own query
 * on the entity, which is what keeps the rule of hooks and the dispatch idiom
 * in the same file honest.
 */
function useRecordFieldOffers(entity: string, id: string) {
  return useKbFigureSeam(entity, id)
}

export function RecordBody({
  entity, id, presentation, onSaved, relations = true, onOpenRelated, headingTestid,
}: RecordBodyProps) {
  /* A slot the record's ⌘ asked to add to — handed to the cascade so the verb
     lands on the governed lookup. The sequence is load-bearing: choosing the
     same verb twice is still two requests, while a bare string state would make
     the second React update a no-op and silently refuse to reopen the picker. */
  const [forcedSlot, setForcedSlot] = useState<{ slot: string; sequence: number } | null>(null)
  const requestRelationSlot = (slot: string) => setForcedSlot((current) => ({
    slot,
    sequence: (current?.sequence ?? 0) + 1,
  }))
  const [customizing, setCustomizing] = useState(false)
  /* #2009 — the authoring TARGET, not the acting persona.
   *
   * This was `'user' | 'persona'`, where "persona" always meant whichever
   * persona the tab happened to be acting as. Two consequences the issue calls
   * out: there was no way to author the DEPLOYMENT default at all, even though
   * `core.set_form_layout` has always admitted `p_persona = null`; and
   * authoring for any other persona meant SWITCHING to it first, which changes
   * what the whole console shows you while you edit a form for someone else.
   *
   * `everyone` carries a null persona. A persona target carries its own key,
   * read from the registry rather than from the session. */
  const [customizingTarget, setCustomizingTarget] = useState<string>('user')
  const [customizingHost, setCustomizingHost] = useState<'console' | 'portal'>('console')
  /* Every registered persona, from the governed `core.list_personas` door —
   * never from the session, and never inferred from what this tab can see. */
  const personaList = usePersonaList()
  /* `user` edits this person's own order; everything else authors a definition.
   * `targetPersona` is null for the deployment default, which is exactly what
   * the write door means by `p_persona = null`. */
  const customizingLayer: 'user' | 'persona' = customizingTarget === 'user' ? 'user' : 'persona'
  const targetPersona: string | null = customizingTarget === 'user' || customizingTarget === 'everyone'
    ? null
    : customizingTarget
  const [saveContractError, setSaveContractError] = useState<Error | null>(null)
  const activePersona = usePersona()
  const layoutDefaults = useFormLayoutDefaults(customizing)
  const setFormLayout = useSetFormLayout()
  const clearFormLayout = useClearFormLayout()
  const toast = useToast()
  const schema = useSchema()
  const sem = useSemantics(entity)
  const exp = useExperience(entity, 'form')
  /* The canonical registry row (#1466). Separate read from the overlay above
     because they are separate stores with separate lifecycles; one may be
     present without the other. */
  const canonicalForm = useCanonicalForm(entity)
  /* WHERE this record is, when the deployment says it has a where (#2276).
     Read off the presentation envelope the record already resolves, so the pin
     and the lens can never come from two different resolutions — and keyed on
     the RECIPE rather than on an entity name, so an entity that gains
     coordinates gets this section with no change here. */
  const tablePresentation = useTablePresentation(entity)
  const mapRecipe = tablePresentation.map
  /* The pin's name is the CANONICAL one — `core.titles_of`, declared by the
     module that owns the entity — so the map calls a record exactly what every
     other surface calls it. Guessing from a column would put a different name on
     the pin than on the row beside it, and 120 of 262 registered entities have
     no name-shaped column at all. Requested only when there is a recipe: a
     record with no map needs no title resolved for one. */
  const locationRefs = useMemo(
    () => (mapRecipe && id !== 'new' ? [{ type: entity, id }] : []), [mapRecipe, entity, id])
  const locationTitles = useRecordTitles(locationRefs)
  const recordTitle = locationTitles.title(entity, id, 'This record')
  /* #1466 — the document's `when` references, settled by the SERVER. Collected
     from whichever document is in force, so a rule named only by the overlay is
     asked about too. */
  const whenRules = useMemo(() => {
    const doc = layoutFromExperience(exp.data)
    return doc ? referencedRules(doc) : []
  }, [exp.data])
  const verdicts = useLayoutRuleVerdicts(entity, id === 'new' ? null : id, whenRules)
  /* #2009 — the AUTHORING read. `useExperienceAtHost` resolves the cascade
     and therefore hands an author a borrowed layer to publish as their own;
     this answers about the exact address. Keyed on the target including null,
     so switching targets cannot reuse the previous one's cached document. */
  const authoring = useAuthoringTarget(entity, targetPersona, customizingHost,
    customizing && customizingLayer === 'persona')
  /* This person's own order for this form (#1466 M4). It is a preference, not
     a definition: it never gates the render, and a settings read that is refused
     leaves the definition's order standing. The in-place composer below is its
     only writer, so user order and persona defaults cannot become competing
     documents.

     `.order`, not the handle. That single omission is what this slice fixes:
     `applyFormOrder` took an all-optional interface, so the handle satisfied it,
     and the rung was inert on every record while the compiler stayed green. The
     type refuses the handle now — see `FormOrder` in `@solum/experience`. */
  const formOrder = useFormOrder(entity)
  const isNew = id === 'new'
  /* The canonical body is the one place every full record and split-pane read
     passes, and its row read IS the evidence (#2444 slice 4): on a SUBJECT
     entity `useEntityRow` reads through `core.subject_get`, which records the
     `record.viewed` in the same transaction that disclosed the row. The
     browser-fired, best-effort read receipt that used to follow it is gone —
     core retired that RPC, and a receipt the browser may skip is not evidence. */
  const rowq = useEntityRow(entity, id)
  const save = useSaveEntity(entity)
  /* Field-level offers, before the query gate for the same reason the identity
     below is: a hook cannot sit behind an early return. It gates its own read
     on the entity, so every other record pays one disabled query. */
  const figures = useRecordFieldOffers(entity, id)

  /* ── The record's identity, computed BEFORE the query gate ────────────────
     Not for the header alone: the palette scope is registered from an effect,
     and an effect cannot sit behind an early return. Both read the same two
     values, so they are derived once, from whatever has arrived. */
  const entityLabel = ((schema.data?.entities ?? []) as { key: string; label: string }[])
    .find((e) => e.key === entity)?.label ?? entity
  const heading = headingFor((rowq.data ?? {}) as Row, singularize(entityLabel), isNew)
  const slots = schema.data ? listSlots(schema.data, entity) : []

  /* The PAGE registers this record as the palette's scope; the pane does not.
     ⌘K answers "what am I looking at?", and beside a list the answer is the
     list — the record is the row you selected, and its own verbs are one click
     away on the mark beside its title. The copilot reads the same scope (#484),
     which is what makes it say "Ask about Northwind…" instead of the URL. */
  useEffect(() => {
    if (presentation !== 'page' || !slots.length) return
    setPaletteScope(recordPaletteScope(entity, id, heading, slots, requestRelationSlot,
      (rid) => onOpenRelated?.(entity, rid)))
    return () => setPaletteScope(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presentation, entity, id, heading, slots.length])

  /* OPENING A RECORD IS VISITING IT (#2267, and it is a capability the ledger
     missed until a browser said so).
     The deleted CRM record page called `recordRecent` on every open, so the
     drawer's recents rail filled as you worked. The canonical route did not, and
     the only thing that noticed was `accounts.spec.ts` asking for "Cocco" in
     `nav-recent` and getting an empty string.
     It belongs HERE rather than at each navigation seam, and that is the
     convergence arriving: the palette, the drawer and the relation picker each
     record a recent because each of them navigates, which leaves a direct URL, a
     bookmark and a redirect recording nothing. The record body is the one place
     every full open passes.
     PAGE only. A peek beside a list is a glance at a row, not a visit — the same
     distinction the palette scope above draws, for the same reason.

     AND ONLY ONCE THE ROW HAS ACTUALLY ARRIVED (#2325 §2: *"a record enters
     Recently opened only after a record route has resolved successfully.
     Failed, refused, missing … are not recorded"*). This used to fire on mount,
     keyed on the route alone, so a pasted link to a deleted id, a record RLS
     refuses, and a typo all landed in the history exactly like a successful
     open — and then vanished from the drawer when `core.titles_of` declined to
     name them, which reads as the history being broken rather than as the
     record being unreachable. `rowq` is the read the body is about to render
     from: `isSuccess` with a row is the resolution, and there is no second
     source of truth about whether this page worked. */
  const resolvedRow = rowq.isSuccess && rowq.data != null
  useEffect(() => {
    if (presentation !== 'page' || isNew || !id || !resolvedRow) return
    recordRecent(entity, id)
  }, [presentation, entity, id, isNew, resolvedRow])

  /* ABOVE THE EARLY RETURN, and that placement is not tidiness — it is the
     rules of hooks. Called after `if (!schema.data …) return null`, this hook
     runs on the second render and not the first, so React throws "rendered more
     hooks than during the previous render" and the record page does not render
     at all. MEASURED: nine browser cases failed that way, and none of them
     mentioned capability — they reported a missing heading, because the whole
     page had crashed.

     `enabled` still avoids the needless round trip: while the schema is unknown
     the query stays off, and an `rpc`-doored entity never asks, because its door
     already decides the answer for every principal. Both of those resolve to
     the capability 'unknown', which fails CLOSED — so the form is read-only
     while it does not know, which is the required direction. */
  const writeCapability = useWriteCapability(
    entity, id,
    !isNew && !!schema.data && writeDoorOf(schema.data, entity) !== 'rpc',
  ).capability
  /* Above the early return for the same reason. One state for the header's
     measures, handed to TWO slots — the disclosure in the identity row and the
     figures in the insight slot (#2406) — so the two cannot disagree. */
  const metrics = useRecordMeasures(entity, !isNew && !!id)

  // A settled-but-empty query rendered a blank surface indistinguishable from
  // "this record has no fields" (UX-03). `exp` is OPTIONAL: an entity with no
  // configured experience returns null, which is a valid answer, not a failure.
  const queries = [schema, sem, ...(isNew ? [] : [rowq])]
  const gate = queryState(queries, [exp])
  if (gate.kind !== 'ready') {
    return (
      <QueryBoundary queries={queries} optional={[exp]} label="this record">
        {() => null}
      </QueryBoundary>
    )
  }

  // Narrowing only — `queryState` above already refuses a settled-without-data
  // query. It exists so TypeScript knows `.data` is present, NOT as the silent
  // blank page it replaced.
  if (!schema.data || !sem.data) return null

  /* The definition first, then this person's permutation of it. `applyFormOrder`
     can only move sections and the fields inside them — `required`, `readOnly`,
     `classification` and the write door are not reachable from a preference —
     and it returns the built view untouched when there is no pin. */
  /* #1466 — THE CANONICAL FORM FIRST, L0 only in its absence.
     `core.form` is the registry every module writes to with `register_form`;
     `document:form` is the LayoutDocument overlay. `useExperience` prefixes
     `document:`, so until now every caller asked for the overlay and the
     registry was resolvable from the server and read by nothing — which is what
     left exp/finding/2 and exp/design/a open.
     Precedence: the canonical form's sections are the DEFINITION (which fields,
     in which sections, in what order); the overlay then places and gates what
     the definition admits. L0 field-meta generation happens only when no form
     is registered. */
  const governedLayout = layoutFromExperience(exp.data)
  const canonicalDocument = canonicalForm.data && typeof canonicalForm.data === 'object'
    && Array.isArray((canonicalForm.data as { sections?: unknown }).sections)
    && ((canonicalForm.data as { sections: unknown[] }).sections.length > 0)
    ? canonicalForm.data as ExperienceDocument
    : undefined
  const legacyDocument = governedLayout && exp.data && typeof exp.data === 'object' && 'layout' in exp.data
    ? exp.data
    : governedLayout ? undefined : exp.data ?? undefined
  const builtView = buildForm(schema.data, sem.data, entity, canonicalDocument ?? legacyDocument)
  const builtLayout = formLayout(builtView)
  /* Filtered through the ONE shared function, not a local `=== 'show'`.
     A missing verdict is unresolved and therefore hidden: the door may not have
     been called, or the answer may not have arrived, and showing a field whose
     condition nobody evaluated is a disclosure rather than a display bug. */
  const gated = governedLayout
    ? applyWhen(governedLayout, verdicts.data ?? [])
    : null
  const whenIssues = gated?.issues ?? []
  /* SURFACED, not merely counted. `unresolved` hides exactly like `hide`, and
     the only thing that distinguishes a deleted rule from a deliberate `false`
     is that one of them says so. The sentence names how many controls are
     affected and never the rule key -- `crm/lead_is_qualified` is an internal
     identifier a person reading a record cannot act on. */
  const whenNotice = whenIssueMessage(whenIssues)
  const definitionView = governedLayout
    ? applyFormLayout(builtView, resolveFormLayout(gated!.document, builtLayout.document, builtLayout.catalog).document)
    : builtView
  const definitionLayoutBase = formLayout(definitionView)
  const orderedView = applyFormOrder(definitionView, formOrder.order)
  const layoutBase = formLayout(orderedView)
  /* The effective document at the target, whoever authored it. It is shown
     because an author needs somewhere to start; `owned_here` below is what
     stops it being MISTAKEN for this address's own. */
  /* #2009 — WHERE this document comes from, said in the editor.
   *
   * Acceptance asks for two things the document itself cannot show: that the
   * UI displays provenance, and that Reset reveals the deterministic inherited
   * layer. Both are facts about the ADDRESS, so they come from the authoring
   * door rather than from anything the composer could infer.
   *
   * Three states worth distinguishing, and a person can act wrongly on each if
   * it is not said:
   *   · this address owns what applies — an ordinary edit;
   *   · this address owns a layer that is SHADOWED by a higher rung — editing
   *     it changes nothing visible, and without this line an author repeats the
   *     edit and concludes the editor is broken;
   *   · this address owns nothing — Save CREATES a layer, freezing a copy of
   *     whatever it currently inherits. That is the clobber, named before it
   *     happens instead of after.
   */
  const target = authoring.data
  /* The `when` notice rides the same slot as provenance, because it is the same
     KIND of statement: something the host has to tell the reader about the
     document in force. It is rendered whether or not anyone is customising —
     a field silently missing matters most to the person just reading. */
  const whenLine = whenNotice ? (
    <p role="status" data-testid="layout-when-unresolved">{whenNotice}</p>
  ) : undefined
  const authoringProvenance = customizingLayer !== 'persona' || !target ? whenLine : (
    <><p data-testid="layout-provenance">
      {target.owned_here && target.effective_is_here && (
        <>This layer is authored here{target.effective?.rung ? ` at the ${target.effective.rung} rung` : ''}.</>
      )}
      {target.owned_here && !target.effective_is_here && (
        <><b>Authored here, but not in effect.</b>{' '}
          {target.effective?.rung
            ? `The ${target.effective.rung} rung outranks it, so your changes will not show until that layer is removed.`
            : 'A higher rung outranks it, so your changes will not show yet.'}</>
      )}
      {!target.owned_here && (
        <><b>Nothing is authored here yet.</b>{' '}
          {target.effective?.rung
            ? `You are looking at the inherited ${target.effective.rung} layer; saving creates a copy of it at this target.`
            : 'Saving creates a layer at this target.'}</>
      )}
      {target.owned_here && (
        <>{' '}Reset reveals {target.lower?.rung
          ? `the inherited ${target.lower.rung} layer`
          : 'the generated form'}.</>
      )}
    </p>{whenLine}</>
  )

  /* WHAT THE EDITOR OPENS, from the governed door and nowhere else.
   *
   * In order: the document at this exact address, then the one it inherits,
   * then the generated form. Each term is a different sentence to the author,
   * and the provenance line above says which one they are reading.
   *
   * This used to prefer `form_layout_defaults()` — a separate inventory read
   * with its own cache — over the authoring envelope, which made the governed
   * answer advisory. Two readers of one fact disagree eventually, and the
   * inventory has no notion of the ladder, so at a SHADOWED address the two
   * disagreed by design.
   *
   * `owned` before `effective` matters at exactly that address: `effective` is
   * then the layer that OUTRANKS this one, and opening it here would put
   * somebody else's document under this address's name — Save would publish it
   * as this address's own. The editor must open the row it is editing. */
  const personaLayout = layoutFromExperience(
    authoringDocument(target) as Parameters<typeof layoutFromExperience>[0],
  ) ?? definitionLayoutBase.document
  const view = orderedView
  /* EFFECTIVE READ-ONLY — three terms, and it fails CLOSED (#2031 prerequisite,
     ruled in mailbox #1958 comment 62517).

         readOnly = the RPC-only structural door
                 OR the governed field's own read_only
                 OR the caller's write capability is not positively TRUE

     The first term is old and unchanged: an `rpc` door means the table's DML is
     revoked, so the Save this form used to draw was answered 403 for EVERYBODY.
     It is a property of the entity and it needs no round trip — the registry
     already said so in the schema query this has just awaited (#877).

     The third term is the new one, and it is the one that was missing. The door
     is structural and identical for every principal, so a person who could SEE
     a record but not write it was offered an editable form whose Save the
     server then refused. The ruling is blunt about that shape — "editable-
     then-refused is not acceptable UX" — and equally blunt about the direction
     it must fail: "Pending/refused capability fails closed and is named; never
     flash editable."

     Hence `!== 'may'` rather than `=== 'may-not'`. While the capability is
     still in flight it is `'unknown'`, and unknown renders READ-ONLY. Treating
     unknown as writable would draw an editable form for the first frames and
     take it away when the answer landed, which is precisely the flash forbidden
     — and on a deployment whose `core.can_act` could not be reached it would
     leave the form editable forever.

     The SECOND term is not computed here: `core.field_meta.read_only` is
     per-field, and `FieldEditor` already ORs it with this form-level answer
     (`fieldEditorBinding`, and `shut = field.readOnly || readOnly`). Recomputing
     it at this level would say the whole form is read-only because one column
     is, which is a different and wrong claim.

     RLS remains the enforcement. This changes what the console DRAWS and
     nothing about what the database permits — which is why the live two-actor
     suite still asserts the transport refusal directly, as defence in depth. */
  const structuralDoor = writeDoorOf(schema.data, entity) === 'rpc'
  const readOnly = structuralDoor || writeCapability !== 'may'
  const row = (rowq.data ?? {}) as Row
  const refResolutions = new Map(view.sections.flatMap((section) => section.fields)
    .filter((field) => field.kind === 'ref')
    .map((field) => [field.field, refTarget(schema.data, entity, field.field)] as const))
  const hiddenRefFields = readOnly ? [] : view.sections.flatMap((section) => section.fields)
    .filter((field) => field.kind === 'ref' && !field.readOnly)
    .flatMap((field) => {
      const resolution = refResolutions.get(field.field)
      return resolution && resolution.kind !== 'none' && resolution.discriminatorColumn
        ? [resolution.discriminatorColumn]
        : []
    })

  /* THE CASCADE, not a tab strip (Christopher, 2026-08-07). Same `BandGrid` the
     relation band renders inside a list row — one mechanism for "show me a
     related list", arranged as stacked sections with the populated ones already
     open. Computed before the return because `<RecordShell>` takes it as a prop:
     `related` present is what makes the shell two columns, so "the page renders
     its own relations below the card" stays expressible as `undefined`. */
  const related = !customizing && relations && !isNew && schema.data
    ? (
      <RelationCascade
        forced={forcedSlot}
        entity={entity} id={id} label={heading}
        /* The form document's own related sections (#1466). `buildForm` used to
           drop `sections[].related` on the floor, so a governed, lint-clean
           declaration named a list no surface ever drew. They lead the cascade
           under the titles the definition wrote; the derived slots still follow,
           because a definition says what MATTERS here, not what exists. */
        declared={declaredRelated(view)}
        onOpenRecord={(e2, id2, n) => onOpenRelated?.(e2, id2, n)}
      />
    )
    : undefined

  return (
    /* ── The record's SHAPE now comes from `@solum/ui` (#964) ───────────────
        The identity bar and master-left/related-right moved to `<RecordShell>`
        verbatim, so `apps/portal`'s records are the same shape rather than an
        imitation of it. What did not move — resolving the form from the
        metamodel, the write door, saving — is still here, because it is what
        this application DOES with a record rather than what a record IS.

        Christopher's canvas model, unchanged and now enforced in one place:
        "each form always has a palette button on top, left of the title of the
        form." Left of the title, the Marble mark IS the record's ⌘: every
        registered relationship as an "Add to …" verb, plus opening a related
        row. */
    <RecordShell
      testId="record-body"
      presentation={presentation}
      heading={heading}
      headingTestId={headingTestid}
      commandTestId="record-relations-cmd"
      commandLabel={`Commands for ${heading}`}
      onCommand={() => openPalette(recordPaletteScope(entity, id, heading, slots,
        requestRelationSlot, (rid) => onOpenRelated?.(entity, rid)))}
      /* The header's two kinds of affordance, and they are not the same kind.
         The governed ACTS come from the server (#1131) and appear on BOTH
         presentations, because a record in a pane is the same record; the
         customize menu is personalization and stays where it was. */
      actions={(!isNew && id) || presentation === 'page' ? (
        <>
          {!isNew && id && <RecordActions entity={entity} id={id} />}
          {presentation === 'page' && (
            <CustomizeRowMenu label={`${heading} form`} actions={[{
              key: 'customize-form', label: 'Customize form', onSelect: () => setCustomizing(true),
            }]} />
          )}
        </>
      ) : undefined}
      /* WHAT this record is, beside its name (#2267 port 5). A slot on the
         shared shell rather than text folded into the heading, so the h1 keeps
         the record's own name as its accessible name. The component answers
         `null` for every entity that is not a party, which is what keeps this
         line free of an `if (entity === …)`. */
      badges={!isNew && id ? <RecordIdentityBadges entity={entity} id={id} /> : undefined}
      badgesTestId="record-identity-badges"
      related={related}
      relatedTestId="record-related"
      /* The record's own governed acts, where the entity has any. `isNew` has
         no row to act on, so the slot stays empty rather than rendering a bar
         over a record that does not exist yet. */
      /* The entity's OWN ceremony first, then the generic declared-machine rail
         (#2267 port 7). The map is for what a rail cannot express — named
         blocker codes spelled into sentences, a confirm before something
         reaches customers — so where a module has written one it wins, and
         every other entity that declares a `core.status_machine` now gets its
         position drawn instead of nothing. */
      process={!isNew && id ? (() => {
        const Process = RECORD_PROCESS[entity]
        return Process
          ? <Process id={id} />
          : <RecordProcess entity={entity} id={id} row={rowq.data as Row | undefined} />
      })() : undefined}
      /* The record's measured context, under its position (#2267 port 6).
         Every figure is computed by `core.dashboard_measures` under the
         caller's own RLS; nothing is reduced here. Its disclosure is in the
         identity row, not above the figures (#2406): collapsed, the cap is
         the identity bar alone. */
      insight={!isNew && id ? <RecordMeasures model={metrics} /> : undefined}
      /* The figures' head row in the cap — "⌄ METRICS … Click to collapse" in
         the leading design (#2406). The shell draws the row; the model says
         whether it is open, what it controls and how to toggle it. Only when
         there ARE figures: no registered measure ⇒ no row. */
      insightDisclosure={metrics.status === 'ready' ? {
        expanded: !metrics.collapsed,
        onToggle: metrics.toggle,
        controls: metrics.stripId,
        label: 'Metrics',
        expandLabel: 'Show record metrics',
        collapseLabel: 'Collapse record metrics',
        testId: 'record-measures-toggle',
      } : undefined}
      /* The facts under the name: the entity's DECLARED subtitle (the same
         `core.titles_of` answer every other surface names this record by) and
         the count the related slots measured. Nothing composed from free
         columns — see `RecordFacts`. */
      facts={!isNew && id ? <RecordFacts entity={entity} id={id} slots={slots} /> : undefined}
      factsTestId="record-facts"
    >
      {customizing ? (
        <LayoutComposer
          key={`${customizingTarget}.${customizingHost}`}
          value={customizingLayer === 'user' ? layoutBase.document : personaLayout}
          catalog={customizingLayer === 'user' ? layoutBase.catalog : definitionLayoutBase.catalog}
          itemLabels={customizingLayer === 'user' ? layoutBase.labels : definitionLayoutBase.labels}
          layerLabel={customizingLayer === 'user'
            ? 'Only me'
            : `${targetPersona ? `${targetPersona} persona` : 'Everyone'} default · ${customizingHost === 'portal' ? 'customer portal' : 'console'}`}
          editMode={customizingLayer === 'user' ? 'order' : 'definition'}
          busy={!formOrder.ready || formOrder.saving || setFormLayout.isPending || clearFormLayout.isPending
            || (customizingLayer === 'persona'
              && (!layoutDefaults.isSuccess || !authoring.isSuccess || !personaList.isSuccess))}
          /* A persona layer needs a persona. Since #1053 4b a LIVE tab starts
             with none chosen (`app/persona.ts`) — the server answers with the
             person's own `user_persona.is_default` — so `activePersona` is ''
             until this tab chooses one. `core.set_form_layout` refuses an
             unknown persona (`packages/core/migrations/2026090609:360`), so
             offering the layer there would render a layer whose name is
             blank, and end in a refusal. The ordinary editor keeps writing the current-user layer;
             the persona default reappears the moment a persona is acting. */
          /* #2009 — offered whenever the DEFINITION reads succeed, not only
             when this tab is acting as a persona. The old gate
             (`activePersona !== ''`) meant a live tab that had chosen no
             persona saw no definition controls at all, and a tab that had
             chosen one could author for that persona only.

             Authority stays server-owned: the gate is that the governed
             defaults and persona registry actually ANSWERED. A refusal means no
             definition controls, and nothing here reads a role or infers
             authority from a persona label. */
          layerControl={layoutDefaults.isSuccess && personaList.isSuccess ? (
            <div data-testid="layout-layer-controls">
              <Select
                value={customizingTarget}
                aria-label="Customization target"
                options={[
                  { value: 'user', label: 'Only me' },
                  /* The deployment default. `core.set_form_layout` has always
                     admitted `p_persona = null`; nothing offered it. */
                  { value: 'everyone', label: 'Everyone (deployment default)' },
                  /* The governed LABEL is what a person reads; the KEY stays
                     the identity (ruling 67896). Two personas may share a
                     display label, so the label must never be the option VALUE
                     — that is the collision. The key is also what
                     `core.set_form_layout` accepts, so the value travels
                     unchanged from this list to the write door. */
                  ...(personaList.data ?? []).map((persona) => ({
                    value: persona.key,
                    label: `${persona.label || persona.key} persona`,
                  })),
                ]}
                onValueChange={setCustomizingTarget}
              />
              {customizingLayer === 'persona' && (
                <Select
                  value={customizingHost}
                  aria-label="Experience host"
                  options={[
                    { value: 'console', label: 'Console' },
                    { value: 'portal', label: 'Customer portal' },
                  ]}
                  onValueChange={(value) => setCustomizingHost(value as 'console' | 'portal')}
                />
              )}
            </div>
          ) : undefined}
          onCancel={() => setCustomizing(false)}
          provenance={authoringProvenance}
          /* Reset removes ONE row. Offering it where no row exists would invite
             a person to "reset" an inherited layer they do not own — which
             either does nothing or, worse, reads as permission to remove
             somebody else's. */
          onReset={customizingLayer === 'persona' && target && !target.owned_here
            ? undefined
            : () => {
            if (customizingLayer === 'user') {
              void formOrder.save(null).then(() => {
                toast.success('Personal form order reset')
                setCustomizing(false)
              }).catch((error: unknown) => toast.error('Couldn\'t reset form order', String((error as Error).message)))
              return
            }
            /* Removes ONLY the exact target row — this persona (or the
               deployment default) at this host. A reset that matched loosely
               would delete another persona's authored layout. */
            clearFormLayout.mutate({ entity, persona: targetPersona, host: customizingHost }, {
              onSuccess: () => {
                toast.success(`${targetPersona ?? 'Everyone'} form default reset`)
                setCustomizing(false)
              },
              onError: (error) => toast.error('Couldn\'t reset persona form', String((error as Error).message)),
            })
          }}
          onSave={(document) => {
            if (customizingLayer === 'user') {
              void formOrder.save(formOrderFromLayout(document)).then(() => {
                toast.success('Personal form order saved')
                setCustomizing(false)
              }).catch((error: unknown) => toast.error('Couldn\'t save form order', String((error as Error).message)))
              return
            }
            setFormLayout.mutate({ entity, persona: targetPersona, host: customizingHost, layout: document }, {
              onSuccess: () => {
                toast.success(`${activePersona} form default saved`)
                setCustomizing(false)
              },
              onError: (error) => toast.error('Couldn\'t save persona form', String((error as Error).message)),
            })
          }}
        />
      ) : <>
        {/* #1744 point 3 on #1679's seam. The picker is a sibling of the form,
            not a child of it: `EntityForm` renders the offer, and the dialog it
            opens belongs to the surface that knows what a knowledge article is. */}
        {figures.picker}
        <EntityForm
        /* The location section, given the form's LIVE values so the preview
           follows uncommitted edits and Cancel restores the persisted place
           for free — see `RecordLocation`'s header for why that falls out of
           reading form state rather than being implemented. */
        aside={mapRecipe
          ? (values) => (
            <RecordLocation
              entity={entity}
              id={id === 'new' ? '' : id}
              recipe={mapRecipe}
              values={values}
              title={recordTitle}
              isNew={id === 'new'}
              readOnly={readOnly}
            />
          )
          : undefined}
        key={`${entity}:${id}`}
        /* The FIELD sections only. A related-only section carries no controls,
           and `EntityForm` would draw it as an empty legend over an empty grid —
           see `fieldSectionsOnly`. Everything else on this surface, the ref
           resolutions included, still reads the whole `view`. */
        view={fieldSectionsOnly(view)}
        row={row}
        isNew={isNew}
        readOnly={readOnly}
        saving={save.isPending}
        fieldActions={figures.fieldActions}
        permittedCapabilities={figures.permittedCapabilities}
        /* Every ref column on every entity becomes a picker — the metamodel
           names the target, and a column it cannot describe fails closed.
           This is the seam `EntityForm` cannot own itself: it must stay
           hook-free for the fixture surfaces. */
        hiddenFields={hiddenRefFields}
        refPicker={(f, value, onChange, values) => {
          const resolution = refResolutions.get(f.field) ?? refTarget(schema.data, entity, f.field)
          const discriminatorColumn = resolution.kind === 'none' ? null : resolution.discriminatorColumn
          const discriminatorValue = discriminatorColumn && values[discriminatorColumn] != null
            ? String(values[discriminatorColumn])
            : null
          return <RefPicker resolution={resolution} column={f.field}
            value={value} discriminatorValue={discriminatorValue} onChange={onChange}
            disabled={readOnly} testid={`ref-${f.field}`} />
        }}
        notice={readOnly
          /* NAMED, per the ruling — and the three terms give different reasons.
             Telling somebody "records of this kind are changed through their
             own actions" when the truth is "you personally may not change this
             one", or when the truth is "I could not ask", sends them to the
             wrong place. */
          ? <ReadOnlyNotice reason={structuralDoor
              ? writeDoorReason(view.title)
              : writeCapability === 'may-not'
                ? capabilityRefusedReason(view.title)
                : capabilityUnknownReason(view.title)} />
          : saveContractError
            ? <div data-testid="save-transition-failure">
                <ErrorState title="The record was saved, but the editor couldn't open"
                  message={saveContractError.message} />
              </div>
          : save.isError
            ? <WriteFailureNotice error={save.error} what={`this ${view.title.toLowerCase()}`} />
            : null}
        onSave={async (patch) => {
          // Deliberately NOT caught here. The rejection is <EntityForm>'s to
          // handle — it is the thing that must decide not to reset the dirty
          // state — and rethrowing is also what keeps the create navigation from
          // firing for a record that was never created (#877).
          setSaveContractError(null)
          const saved = await save.mutateAsync({ id, patch })
          let savedId: string
          try {
            savedId = savedRecordId(id, isNew, saved)
          } catch (cause) {
            const error = cause instanceof Error ? cause : new Error(String(cause))
            setSaveContractError(error)
            throw error
          }
          return savedId
        }}
        afterSave={onSaved
          ? async (savedId, intent) => onSaved({ id: String(savedId), intent, created: isNew })
          : undefined}
        />
        {/* Below the form, and only on a record that exists: a preview of a row
            nobody has saved yet would be a preview of nothing. */}
        {!isNew && id && (() => {
          const Reading = RECORD_READING[entity]
          /* ONE rhythm for the readings, declared where they MOUNT (#2349 §C).
             Each panel used to carry its own `margin-top`, so the distance
             between the form and the reading below it was a per-file decision —
             #2283's own complaint, one surface further in. They are
             `PageSection`s now, and `.section + .section` owns the space
             between two of them; what this wrapper owns is the space between
             the FORM and the first of them. */
          /* System information closes every existing record (#2444 phase 4):
             the row's own timestamps and where its last change came from. It
             rides the readings' mount so it takes the same rhythm. */
          return (
            <div className={s.readings}>
              {Reading ? <Reading id={id} /> : null}
              <RecordSystemInfo entity={entity} id={id} row={row} />
            </div>
          )
        })()}
      </>}
    </RecordShell>
  )
}
